import ManageAiVoicesModal from '@src/components/aiVoices/ManageAiVoicesModal';
import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { ToggleGroup } from '@src/components/common/ToggleGroup';
import { TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import AssignSpeakerModal from '@src/components/sidepanel/modals/AssignSpeakerModal';
import CreatorStudioSpeakerIdentificationModal from '@src/components/sidepanel/modals/CreatorStudioSpeakerIdentificationModal';
import SpeakerContextMenu from '@src/components/transcript/SpeakerContextMenu';
import SpeakerFormModal from '@src/components/transcript/SpeakerFormModal';
import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import {
  getSpeakerColor,
  reassignOverridesToVoiceLabel,
  undoSectionOverride,
} from '@src/services/SpeakerManagementService';
import {
  type AnonymousSpeaker,
  resolveAssignedSpeakers,
  resolveTranscriptSpeakers,
  type SpeakerData,
  type SpeakerWithColor,
} from '@src/services/SpeakerManagementService';
import {
  cacheAndWriteSpeakerAssignment,
  clearSpeakerCache,
  notifySpeakerCacheUpdated,
  SPEAKER_ADDED_TO_STORY_EVENT,
  updateSpeakerNameInCache,
} from '@src/stores-v2/speakerCache';
import {
  useDatoClient,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import AiSpeakerIcon from '@src/svgs/AiSpeakerIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import PlusIcon from '@src/svgs/PlusIcon';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  getSpeakerColorFromLabel,
  getSpeakerNameFromLabel,
} from '@src/utility/speakerUtils';
import { dispatchTranscriptSeek } from '@src/utility/transcriptEvents';
import {
  findCurrentOrNextSpeakerSection,
  findNextSpeakerSection,
  findPreviousSpeakerSection,
  groupElementsByParagraph,
} from '@src/utility/transcriptGrouping';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { VideoTimeRange } from '@src/components/content-components/TranscriptSpeakersTab/TranscriptVideoPlayer';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { MouseEvent, ReactNode, RefObject } from 'react';

const logger = LogManager.getLogger('component:CreatorStudioSpeakersPanel');

// Optimistic placeholder row shown while an "Add Speaker" save runs in the
// background (after the modal has already closed).
interface PendingSpeaker {
  tempId: string;
  name: string;
  title?: string;
  color: string;
  previewUrl?: string;
  status: 'saving' | 'error';
  // Re-runs the save with the same (already-uploaded) data. Captured at submit
  // time so a retry preserves the assignment context the modal had.
  retrySave?: () => Promise<boolean>;
}

type TabType = 'story' | 'organization';

/**
 * Speaker management panel for Creator Studio sidebar.
 *
 * Story tab shows speakers split into two sections:
 * - Assigned: Speakers whose Dato ID appears as a VALUE in speaker-voice mappings
 *   (meaning a transcript voice like spk_0 is mapped to them)
 * - Unassigned: Speakers in the story that haven't been mapped to any transcript voice yet
 *
 * Organization tab shows all speakers from the album/organization with option to add to story.
 */
interface Props {
  // When true, speaker navigation searches the full original transcript instead
  // of slicing to videoCreator.currentVideo.clipJson.transcriptPosition.
  // Required for Content Studio's Transcript & Speakers tab, where the player
  // shows story.originalVideo while currentVideo may be a stale clip.
  useOriginalTranscript?: boolean;
  // The creator whose player the prev/next speaker navigation drives. Hosts
  // that run on their own scoped adapter (Content Studio's Transcript &
  // Speakers tab, the Translation Review workspace) MUST pass it: their player
  // is bound to that adapter, and both the reference time and the scoped
  // 'transcript-seek' event (PLAT-10286 compares by object identity) come from
  // it — read off the global store instead, the arrows measure a clock nothing
  // advances and dispatch a seek every mounted player drops. Defaults to the
  // global store for Creator Studio's sidebar, which owns it.
  videoCreator?: ITranscriptVideoCreator;
  // The host creator's playback window, when it has one. A clip-scoped host
  // (the clip-preview Translation Review) plays the whole story video bounded
  // to this story-absolute window, and its clock is RANGE-LOCAL — 0 is the
  // window's start (TranscriptVideoPlayer's `timeRange`). Transcript targets
  // here are always story-absolute, so without the window the two spaces are
  // mixed: the seek is clamped to the window's length and lands on its last
  // frame (PLAT-11389). Absent = the host counts from the story's start.
  timeRange?: VideoTimeRange;
  /**
   * PLAT-11877 — the host's own action, on its own row above the Story /
   * Organization toggle (Content Studio's Transcript & Languages tab puts
   * Produce Clips here). Rendered as given.
   */
  headerActions?: ReactNode;
}

const CreatorStudioSpeakersPanel = observer((props: Props) => {
  const { useOriginalTranscript = false } = props;
  const videoCreator = useVideoCreatorStore();
  const playbackCreator = props.videoCreator ?? videoCreator;
  // Story-absolute time <-> the host clock's space. 0 for every unbounded host.
  const hostTimeOffset = props.timeRange?.start ?? 0;
  const datoClientStore = useDatoClient();
  const transcriptionStore = useTranscription();
  const userIdentity = useUserIdentity();
  const speakerService = useSpeakerManagementService();
  const { enableSpeakersIdentification, enableAiVoices } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const { dubbingEnabled } = useVoiceActivation();

  const [activeTab, setActiveTab] = useState<TabType>('story');
  const idSpeakerTooltipId = `id-speaker-${useId()}`;
  const [assignedSpeakers, setAssignedSpeakers] = useState<SpeakerWithColor[]>(
    [],
  );
  const [unassignedSpeakers, setUnassignedSpeakers] = useState<
    SpeakerWithColor[]
  >([]);
  const [organizationSpeakers, setOrganizationSpeakers] = useState<
    SpeakerWithColor[]
  >([]);
  const [anonymousSpeakers, setAnonymousSpeakers] = useState<
    AnonymousSpeaker[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showIdentificationModal, setShowIdentificationModal] = useState(false);
  // Optimistic "saving" rows for in-flight Add Speaker creates (modal closes
  // immediately; the save resolves in the background).
  const [pendingSpeakers, setPendingSpeakers] = useState<PendingSpeaker[]>([]);
  const pendingIdRef = useRef(0);

  // Context menu and edit state
  const [selectedSpeaker, setSelectedSpeaker] =
    useState<SpeakerWithColor | null>(null);
  const [selectedAnonymousSpeaker, setSelectedAnonymousSpeaker] =
    useState<AnonymousSpeaker | null>(null);
  const [activeButtonRef, setActiveButtonRef] =
    useState<RefObject<HTMLElement> | null>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  // Speaker whose "Manage AI Voices" modal is open (null = closed).
  const [manageVoicesSpeaker, setManageVoicesSpeaker] =
    useState<SpeakerWithColor | null>(null);
  // Track which speaker IDs are assigned via voice mapping (spk_X → datoId)
  // vs override only (override_<datoId>) — used to show correct context menu options
  const [voiceMappedDatoIds, setVoiceMappedDatoIds] = useState<Set<string>>(
    new Set(),
  );
  const [activeOverrideLabels, setActiveOverrideLabels] = useState<string[]>(
    [],
  );
  const [speakerToEdit, setSpeakerToEdit] = useState<SpeakerWithColor | null>(
    null,
  );
  const [speakerLabelToAssign, setSpeakerLabelToAssign] = useState<
    string | null
  >(null);

  // Speaker mappings for reverse lookup (speakerLabel -> datoSpeakerId)
  const [speakerMappings, setSpeakerMappings] = useState<Record<
    string,
    string
  > | null>(null);
  // Ref to latest mappings for use in callbacks that may capture stale closure
  const speakerMappingsRef = useRef(speakerMappings);
  speakerMappingsRef.current = speakerMappings;

  // Refresh counter to trigger re-fetch when needed (e.g., after external changes)
  const [refreshCounter, setRefreshCounter] = useState(0);
  // Track recent local updates to suppress stale re-fetches (e.g., modal close triggers focus)
  // Timestamp of last optimistic update. Suppresses CDN re-fetches for 3s
  // to prevent stale data from overwriting the optimistic state.
  const recentUpdateRef = useRef(0);

  // Get storyId and albumId from videoCreator
  const storyId = videoCreator.story?.id;
  const albumId = videoCreator.story?.primaryShowcase?.id;

  // Both window focus (user switches back from Content Studio) and speaker cache
  // invalidation (transcript side reassigns a speaker) trigger the same refresh,
  // so they share a single handler.
  useEffect(() => {
    const triggerRefresh = () => {
      // Skip focus-triggered re-fetches right after a local update to avoid
      // DatoCMS CDN returning stale data and overwriting the local state.
      const msSinceUpdate = Date.now() - recentUpdateRef.current;
      if (recentUpdateRef.current > 0 && msSinceUpdate < 3000) {
        logger.info('Skipping refresh — recent local update', {
          msSinceUpdate,
        });
        return;
      }
      setRefreshCounter(prev => prev + 1);
    };

    // Optimistic update when a speaker is added to the story from another component
    // (e.g. global reassign in Transcript.tsx). Avoids waiting for DatoCMS CDN propagation.
    const handleSpeakerAdded = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.speaker) {
        const s: SpeakerWithColor = detail.speaker;
        const speakerWithColor: SpeakerWithColor = {
          ...s,
          title: s.title || '',
        };
        const replacedId = detail.replacedSpeakerId;
        setAssignedSpeakers(prev => {
          let next = prev.some(x => x.id === s.id)
            ? prev
            : [...prev, speakerWithColor];
          // If a speaker was replaced (reassign), move them to unassigned
          if (replacedId && replacedId !== s.id) {
            const replaced = next.find(x => x.id === replacedId);
            if (replaced) {
              next = next.filter(x => x.id !== replacedId);
              setUnassignedSpeakers(p => [...p, replaced]);
            }
          }
          return next;
        });
        setUnassignedSpeakers(prev => prev.filter(x => x.id !== s.id));
        setVoiceMappedDatoIds(prev => {
          const next = new Set([...prev, s.id]);
          if (replacedId) next.delete(replacedId);
          return next;
        });
        // Remove anonymous speakers whose labels were just mapped
        const labels: string[] | undefined =
          detail.mappedLabels ||
          (detail.mappedLabel ? [detail.mappedLabel] : undefined);
        if (labels && labels.length > 0) {
          const labelSet = new Set(labels);
          setAnonymousSpeakers(prev =>
            prev.filter(a => !labelSet.has(a.speakerLabel)),
          );
        }
        // Suppress the next CDN re-fetch so stale data doesn't overwrite this update
        recentUpdateRef.current = Date.now();
      }
    };

    window.addEventListener('focus', triggerRefresh);
    window.addEventListener('speaker-cache-invalidated', triggerRefresh);
    window.addEventListener(SPEAKER_ADDED_TO_STORY_EVENT, handleSpeakerAdded);
    return () => {
      window.removeEventListener('focus', triggerRefresh);
      window.removeEventListener('speaker-cache-invalidated', triggerRefresh);
      window.removeEventListener(
        SPEAKER_ADDED_TO_STORY_EVENT,
        handleSpeakerAdded,
      );
    };
  }, []);

  // Fetch speakers when component mounts, storyId changes, or refresh is triggered
  useEffect(() => {
    let cancelled = false;

    const fetchSpeakers = async () => {
      if (!storyId) {
        setIsLoading(false);
        return;
      }

      // Only show loading spinner on initial load, not on background refreshes
      if (refreshCounter === 0) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await speakerService.fetchSpeakersForStory(storyId);

        if (cancelled) return;

        if (!result.ok) {
          setError(result.error.message);
          return;
        }

        const {
          storySpeakers,
          orgSpeakers,
          mappings: fetchedMappings,
        } = result.value;

        // Store mappings for reverse lookup during unassign
        setSpeakerMappings(fetchedMappings);

        // Voice-mapped ids alone still matter for the unassign path (a speaker
        // with a mapping is unassigned by clearing it, one with only an
        // override by restoring the passage labels).
        setVoiceMappedDatoIds(
          new Set(Object.values(fetchedMappings).filter(Boolean)),
        );

        // Assigned = mapped OR section-override speakers. Shared with the dub
        // modal so the two surfaces cannot drift (PLAT-11583).
        const { assigned, unassigned, overrideLabels } =
          resolveAssignedSpeakers({
            storyId,
            storySpeakers,
            mappings: fetchedMappings,
            transcriptElements:
              transcriptionStore?.getOriginalTranscription()?.elements || [],
          });
        setActiveOverrideLabels(overrideLabels);

        // Extract anonymous speakers from transcript
        // These are spk_X labels that either have no mapping or have empty/unmapped mapping
        // Check both final elements AND original transcript (Content Studio uses originalTranscription)
        const allTranscriptElements =
          transcriptionStore?.getFinalTranscriptionElements() || [];
        const originalElements =
          transcriptionStore?.getOriginalTranscription()?.elements || [];
        const clipRange =
          videoCreator.currentVideo?.clipJson?.transcriptPosition;
        const relevantElements = clipRange
          ? allTranscriptElements.slice(
              clipRange.startIndex,
              clipRange.endIndex + 1,
            )
          : allTranscriptElements;
        // Merge labels from both sources to catch all speakers
        const { anonymous } = resolveTranscriptSpeakers({
          elementLists: [relevantElements, originalElements],
          mappings: fetchedMappings,
        });

        setAssignedSpeakers(assigned);
        setUnassignedSpeakers(unassigned);
        setOrganizationSpeakers(orgSpeakers);
        setAnonymousSpeakers(anonymous);
      } catch (err) {
        if (cancelled) return;
        logger.error('Error fetching speakers:', err);
        setError('Failed to load speakers');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSpeakers();

    return () => {
      cancelled = true;
    };
  }, [storyId, speakerService, transcriptionStore, refreshCounter]);

  // All story speakers (assigned + unassigned) for checking if speaker is in story
  const allStorySpeakers = useMemo(() => {
    const seen = new Set<string>();
    return [...assignedSpeakers, ...unassignedSpeakers].filter(s =>
      seen.has(s.id) ? false : (seen.add(s.id), true),
    );
  }, [assignedSpeakers, unassignedSpeakers]);

  // Handle adding speaker to story from organization
  const handleAddToStory = useCallback(
    async (speaker: SpeakerWithColor) => {
      if (!storyId || !datoClientStore?.speakerRepository) return;

      try {
        await datoClientStore.speakerRepository.addSpeakerReferenceToStory(
          speaker.id,
          storyId,
        );

        // Update local state - new speakers from org go to unassigned (not mapped yet)
        setUnassignedSpeakers(prev => [...prev, speaker]);

        // Switch to story tab to show the added speaker
        setActiveTab('story');
      } catch (err) {
        logger.error('Error adding speaker to story:', err);
      }
    },
    [storyId, datoClientStore?.speakerRepository],
  );

  // Check if a speaker is already in the story
  const isSpeakerInStory = useCallback(
    (speakerId: string) => {
      return allStorySpeakers.some(s => s.id === speakerId);
    },
    [allStorySpeakers],
  );

  // Handler for speaker overflow button click - opens context menu
  const handleOverflowButtonClick = useCallback(
    (e: MouseEvent, speaker: SpeakerWithColor) => {
      e.stopPropagation();
      // Create a NEW ref object on each click - this is important because
      // SpeakerContextMenu uses buttonRef in its useEffect dependencies
      const buttonPositionRef = { current: e.currentTarget as HTMLElement };
      setActiveButtonRef(buttonPositionRef as RefObject<HTMLElement>);
      setSelectedSpeaker(speaker);
      setSelectedAnonymousSpeaker(null); // Clear anonymous speaker selection
      setShowAssignModal(false); // Ensure assign modal is closed
      setContextMenuOpen(true);
    },
    [],
  );

  // Handler for anonymous speaker overflow button click - opens context menu for "Assign Speaker"
  const handleAnonymousOverflowButtonClick = useCallback(
    (e: MouseEvent, speaker: AnonymousSpeaker) => {
      e.stopPropagation();
      const buttonPositionRef = { current: e.currentTarget as HTMLElement };
      setActiveButtonRef(buttonPositionRef as RefObject<HTMLElement>);
      setSelectedAnonymousSpeaker(speaker);
      setSelectedSpeaker(null); // Clear regular speaker selection
      setShowAssignModal(false); // Ensure assign modal is closed
      setContextMenuOpen(true);
    },
    [],
  );

  // Handler for closing the context menu
  const handleCloseContextMenu = useCallback(() => {
    setContextMenuOpen(false);
    setSelectedSpeaker(null);
    setSelectedAnonymousSpeaker(null);
    setActiveButtonRef(null);
  }, []);

  // Handler for editing a speaker - opens modal in edit mode
  // Also handles "Assign Speaker" for anonymous speakers
  const handleEditSpeaker = useCallback(() => {
    setContextMenuOpen(false);
    if (selectedSpeaker) {
      // Regular speaker - open edit modal
      setSpeakerToEdit(selectedSpeaker);
      setSpeakerLabelToAssign(null);
      setShowSpeakerModal(true);
    } else if (selectedAnonymousSpeaker) {
      // Anonymous speaker - open create modal to assign
      setSpeakerToEdit(null);
      setSpeakerLabelToAssign(selectedAnonymousSpeaker.speakerLabel);
      setShowSpeakerModal(true);
    }
  }, [selectedSpeaker, selectedAnonymousSpeaker]);

  // Handler for opening the Manage AI Voices modal for the selected speaker.
  const handleManageAiVoices = useCallback(() => {
    setContextMenuOpen(false);
    if (selectedSpeaker) setManageVoicesSpeaker(selectedSpeaker);
    setSelectedSpeaker(null);
  }, [selectedSpeaker]);

  // Handler for unassigning a speaker (removes voice mapping but keeps speaker in story)
  const handleUnassignSpeaker = useCallback(async () => {
    if (!selectedSpeaker || !storyId || !speakerMappings) {
      return;
    }

    // Find the speakerLabel that maps to this Dato speaker ID (reverse lookup)
    // Use ref to get latest mappings (avoids stale closure when unassigning multiple speakers quickly)
    const currentMappings = speakerMappingsRef.current || speakerMappings;
    const speakerLabel = Object.entries(currentMappings).find(
      ([, datoId]) => datoId === selectedSpeaker.id,
    )?.[0];

    if (!speakerLabel) {
      logger.error(
        'Could not find speaker label for Dato ID:',
        selectedSpeaker.id,
      );
      return;
    }

    const result = await speakerService.unassignSpeaker(storyId, speakerLabel);

    if (result.ok) {
      // Write-through: revert cached label to fallback name/color so the
      // transcript UI updates immediately without waiting for a re-fetch.
      cacheAndWriteSpeakerAssignment(
        storyId,
        speakerLabel,
        getSpeakerNameFromLabel(speakerLabel),
        getSpeakerColorFromLabel(speakerLabel),
      );
      notifySpeakerCacheUpdated(storyId);

      // Re-fetch all speaker data from server to ensure consistent state
      // (avoids stale closure issues when rapidly unassigning multiple speakers)
      setRefreshCounter(prev => prev + 1);
    } else {
      logger.error('Error unassigning speaker:', result.error);
    }

    // Close the context menu
    setContextMenuOpen(false);
    setSelectedSpeaker(null);
  }, [selectedSpeaker, storyId, speakerMappings, speakerService]);

  // Handler for undoing a section-level override assignment
  const handleUndoSectionReassign = useCallback(async () => {
    if (!selectedSpeaker || !storyId || !transcriptionStore) return;

    await undoSectionOverride(
      selectedSpeaker.id,
      storyId,
      transcriptionStore,
      datoClientStore?.speakerRepository,
    );

    setRefreshCounter(prev => prev + 1);
    setContextMenuOpen(false);
    setSelectedSpeaker(null);
  }, [selectedSpeaker, storyId, transcriptionStore, datoClientStore]);

  // Handler for unassigning both voice mapping AND section overrides
  const handleUnassignAll = useCallback(async () => {
    if (!selectedSpeaker || !storyId || !transcriptionStore) return;

    // 1. Unassign voice mapping (if exists)
    if (voiceMappedDatoIds.has(selectedSpeaker.id) && speakerMappings) {
      const currentMappings = speakerMappingsRef.current || speakerMappings;
      const speakerLabel = Object.entries(currentMappings).find(
        ([, datoId]) => datoId === selectedSpeaker.id,
      )?.[0];
      if (speakerLabel) {
        await speakerService.unassignSpeaker(storyId, speakerLabel);
        // Write-through: revert cached label to fallback name/color
        cacheAndWriteSpeakerAssignment(
          storyId,
          speakerLabel,
          getSpeakerNameFromLabel(speakerLabel),
          getSpeakerColorFromLabel(speakerLabel),
        );
      }
    }

    // 2. Undo section overrides via service
    await undoSectionOverride(selectedSpeaker.id, storyId, transcriptionStore);

    // Notify after both voice unassign and override undo are done
    notifySpeakerCacheUpdated(storyId);
    setRefreshCounter(prev => prev + 1);
    setContextMenuOpen(false);
    setSelectedSpeaker(null);
  }, [
    selectedSpeaker,
    storyId,
    voiceMappedDatoIds,
    speakerMappings,
    speakerService,
    transcriptionStore,
  ]);

  // Handler for converting section overrides into the voice-mapped label
  const handleReassignAll = useCallback(async () => {
    if (!selectedSpeaker || !storyId || !speakerMappings || !transcriptionStore)
      return;

    const currentMappings = speakerMappingsRef.current || speakerMappings;
    const voiceLabel = Object.entries(currentMappings).find(
      ([, id]) => id === selectedSpeaker.id,
    )?.[0];
    if (!voiceLabel) return;

    reassignOverridesToVoiceLabel(
      selectedSpeaker.id,
      voiceLabel,
      transcriptionStore,
      storyId,
    );

    setRefreshCounter(prev => prev + 1);
    setContextMenuOpen(false);
    setSelectedSpeaker(null);
  }, [selectedSpeaker, storyId, speakerMappings, transcriptionStore]);

  // Handler for removing a speaker from story (removes speaker reference from DatoCMS)
  const handleRemoveFromStory = useCallback(async () => {
    if (!selectedSpeaker || !storyId || !datoClientStore?.speakerRepository) {
      return;
    }

    // Optimistic UI update — remove from lists immediately
    const speakerId = selectedSpeaker.id;
    setAssignedSpeakers(prev => prev.filter(s => s.id !== speakerId));
    setUnassignedSpeakers(prev => prev.filter(s => s.id !== speakerId));
    handleCloseContextMenu();

    // Persist to DatoCMS in background
    try {
      await datoClientStore.speakerRepository.removeSpeakerReferenceFromStoryOrAlbum(
        speakerId,
        storyId,
      );
    } catch (err) {
      logger.error('Error removing speaker from story:', err);
      // Re-fetch to restore correct state on failure
      setRefreshCounter(prev => prev + 1);
    }
  }, [
    selectedSpeaker,
    storyId,
    datoClientStore?.speakerRepository,
    handleCloseContextMenu,
  ]);

  // Handler for removing a speaker from organization (removes speaker reference from album/showcase in DatoCMS)
  const handleRemoveFromOrganization = useCallback(() => {
    if (!selectedSpeaker || !albumId || !datoClientStore?.speakerRepository) {
      return;
    }

    const speakerId = selectedSpeaker.id;

    // Optimistic update — remove from UI immediately
    setOrganizationSpeakers(prev => prev.filter(s => s.id !== speakerId));
    handleCloseContextMenu();

    // Suppress stale focus-triggered re-fetches while DatoCMS CDN catches up
    recentUpdateRef.current = Date.now();

    // Invalidate speaker cache so other consumers (e.g. transcript view) get fresh data
    clearSpeakerCache(storyId);

    // Fire DatoCMS update in the background
    datoClientStore.speakerRepository
      .removeSpeakerReferenceFromStoryOrAlbum(speakerId, albumId)
      .catch(err => {
        logger.error('Error removing speaker from organization:', err);
      });
  }, [
    selectedSpeaker,
    storyId,
    albumId,
    datoClientStore?.speakerRepository,
    handleCloseContextMenu,
  ]);

  // Handle creating a new speaker (and optionally assigning to an anonymous speaker)
  const handleCreateSpeaker = useCallback(
    async (speakerData: SpeakerData): Promise<boolean> => {
      if (!storyId) {
        logger.error('No story loaded');
        return false;
      }

      if (speakerLabelToAssign) {
        // Create and assign to anonymous speaker label in one operation
        const label = speakerLabelToAssign;
        const result = await speakerService.createAndAssignSpeaker(
          storyId,
          albumId,
          label,
          speakerData,
          speakerMappings || {},
        );

        if (result.ok) {
          // Optimistic update: the just-created speaker isn't visible from the
          // DatoCMS CDA yet, so a re-fetch would drop it (the anonymous row
          // disappears because the mapping exists, but the speaker never lands
          // in Assigned). Mirror the assign-to-existing path and update local
          // state directly.
          const newSpeaker = result.value;
          const color = getSpeakerColorFromLabel(label);
          const speakerWithColor: SpeakerWithColor = {
            ...newSpeaker,
            color,
            // Use the just-uploaded headshots ({ id, url }); the CMA create
            // response's headshots are { upload_id } with no displayable url.
            headshots: speakerData.headshots ?? undefined,
          };
          setAssignedSpeakers(prev =>
            prev.some(s => s.id === newSpeaker.id)
              ? prev
              : [...prev, speakerWithColor],
          );
          setAnonymousSpeakers(prev =>
            prev.filter(s => s.speakerLabel !== label),
          );
          setVoiceMappedDatoIds(prev => new Set([...prev, newSpeaker.id]));
          setSpeakerMappings(prev => ({
            ...(prev || {}),
            [label]: newSpeaker.id,
          }));

          // Write-through the label cache so the transcript view resolves the
          // new name/color immediately.
          cacheAndWriteSpeakerAssignment(
            storyId,
            label,
            newSpeaker.name,
            color,
          );
          notifySpeakerCacheUpdated(storyId);

          // Suppress the stale focus-triggered re-fetch (CDA propagation lag).
          recentUpdateRef.current = Date.now();

          setSpeakerLabelToAssign(prev => (prev === label ? null : prev));
          setActiveTab('story');
          return true;
        } else {
          logger.error('Error creating and assigning speaker', {
            errorCode: result.error.code,
            errorMessage: result.error.message,
            speakerLabel: speakerLabelToAssign,
            headshotCount: speakerData.headshots?.length ?? 0,
          });
          return false;
        }
      } else {
        // Normal create without assignment — use service's createAndAssignSpeaker
        // with empty speakerLabel so no mapping is created
        const result = await speakerService.createAndAssignSpeaker(
          storyId,
          albumId,
          '', // No speaker label to assign
          speakerData,
          speakerMappings || {},
        );

        if (result.ok) {
          // Optimistic update — a re-fetch alone would drop the new speaker
          // until the DatoCMS CDA propagates. New speakers go to Unassigned.
          const newSpeaker = result.value;
          const speakerWithColor: SpeakerWithColor = {
            ...newSpeaker,
            color: getSpeakerColor(newSpeaker.id, allStorySpeakers.length),
            // Use the just-uploaded headshots ({ id, url }); the CMA create
            // response's headshots are { upload_id } with no displayable url.
            headshots: speakerData.headshots ?? undefined,
          };
          setUnassignedSpeakers(prev =>
            prev.some(s => s.id === newSpeaker.id)
              ? prev
              : [...prev, speakerWithColor],
          );
          recentUpdateRef.current = Date.now();
          setActiveTab('story');
          return true;
        } else {
          logger.error('Error creating speaker', {
            errorCode: result.error.code,
            errorMessage: result.error.message,
            headshotCount: speakerData.headshots?.length ?? 0,
          });
          return false;
        }
      }
    },
    [
      storyId,
      albumId,
      allStorySpeakers.length,
      speakerLabelToAssign,
      speakerMappings,
      speakerService,
    ],
  );

  // Background "Add Speaker" handler — the modal closes immediately and hands
  // off the headshot upload + create. We show a skeleton "saving" row until the
  // save resolves, then swap it for the real speaker (or surface an error).
  const handleCreateSpeakerBackground = useCallback(
    (args: {
      data: Omit<SpeakerData, 'headshots'>;
      uploadPromise: Promise<
        Array<{ id: string; url: string }> | null | undefined
      >;
      previewUrls: string[];
    }) => {
      const tempId = `pending_${(pendingIdRef.current += 1)}`;
      // Capture the assignment context now: speakerLabelToAssign is reset when
      // the modal closes, but a retry must still use the value from submit time.
      const labelToAssign = speakerLabelToAssign;
      const color = labelToAssign
        ? getSpeakerColorFromLabel(labelToAssign)
        : getSpeakerColor(tempId, allStorySpeakers.length);
      // Bind the create at submit time so it keeps the correct speakerLabelToAssign.
      const boundCreate = handleCreateSpeaker;

      const finalize = (speakerData: SpeakerData): (() => Promise<boolean>) => {
        const run = async () => {
          const ok = await boundCreate(speakerData);
          if (ok) {
            setPendingSpeakers(prev => prev.filter(p => p.tempId !== tempId));
          } else {
            setPendingSpeakers(prev =>
              prev.map(p =>
                p.tempId === tempId
                  ? { ...p, status: 'error', retrySave: finalize(speakerData) }
                  : p,
              ),
            );
          }
          return ok;
        };
        return run;
      };

      setPendingSpeakers(prev => [
        ...prev,
        {
          tempId,
          name: args.data.name,
          title: args.data.title,
          color,
          previewUrl: args.previewUrls[0],
          status: 'saving',
        },
      ]);

      void (async () => {
        let headshots: Array<{ id: string; url: string }> | null | undefined;
        try {
          headshots = await args.uploadPromise;
        } catch (err) {
          logger.error(
            'Headshot upload failed during background create',
            extractErrorInfo(err),
          );
          headshots = undefined;
        }
        await finalize({ ...args.data, headshots: headshots ?? undefined })();
      })();
    },
    [speakerLabelToAssign, allStorySpeakers.length, handleCreateSpeaker],
  );

  // Retry a failed background create (data + uploaded headshots already captured).
  const handleRetryPendingSpeaker = useCallback((pending: PendingSpeaker) => {
    if (!pending.retrySave) return;
    setPendingSpeakers(prev =>
      prev.map(p =>
        p.tempId === pending.tempId ? { ...p, status: 'saving' } : p,
      ),
    );
    void pending.retrySave();
  }, []);

  // Dismiss a failed background create row.
  const handleDismissPendingSpeaker = useCallback((tempId: string) => {
    setPendingSpeakers(prev => prev.filter(p => p.tempId !== tempId));
  }, []);

  // Handler for updating an existing speaker
  const handleUpdateSpeaker = useCallback(
    async (speakerData: SpeakerData): Promise<boolean> => {
      if (!speakerToEdit) {
        return false;
      }

      const result = await speakerService.updateSpeaker(
        speakerToEdit.id,
        speakerData,
      );

      if (result.ok) {
        // Update local state with the new speaker data
        const updateSpeakerInList = (speakers: SpeakerWithColor[]) =>
          speakers.map(s => {
            if (s.id === speakerToEdit.id) {
              return {
                ...s,
                name: speakerData.name ?? s.name,
                title: speakerData.title ?? s.title,
                bio: speakerData.bio ?? s.bio,
                voice: speakerData.voice ?? s.voice,
                gender: speakerData.gender || null,
                headshots: speakerData.headshots ?? s.headshots,
              };
            }
            return s;
          });

        setAssignedSpeakers(updateSpeakerInList);
        setUnassignedSpeakers(updateSpeakerInList);
        setOrganizationSpeakers(updateSpeakerInList);

        // Suppress the next focus-triggered re-fetch so DatoCMS CDN staleness
        // doesn't overwrite the local state we just set.
        recentUpdateRef.current = Date.now();

        // Update the useSpeakerLabels hook cache in-place so the transcript
        // view reflects the new name immediately (avoids stale CDN re-fetch).
        // Pass datoSpeakerId so override_<id> labels are also updated.
        if (storyId) {
          const mappings = speakerMappingsRef.current;
          const label = mappings
            ? Object.entries(mappings).find(
                ([, id]) => id === speakerToEdit.id,
              )?.[0]
            : undefined;
          updateSpeakerNameInCache(
            storyId,
            label ?? '',
            speakerData.name,
            speakerToEdit.id,
          );
        }

        return true;
      } else {
        logger.error('Error updating speaker:', result.error);
        return false;
      }
    },
    [speakerToEdit, speakerService, storyId],
  );

  // Handler for closing the modal - resets edit state
  const handleModalClose = useCallback(() => {
    setShowSpeakerModal(false);
    setSpeakerToEdit(null);
    setSpeakerLabelToAssign(null);
  }, []);

  // Handler for opening add speaker modal
  const handleOpenAddSpeakerModal = useCallback(() => {
    setSpeakerToEdit(null);
    setSpeakerLabelToAssign(null);
    setShowSpeakerModal(true);
  }, []);

  // Handler for opening the assign speaker modal (for anonymous speakers)
  const handleOpenAssignModal = useCallback(() => {
    setContextMenuOpen(false);
    setShowAssignModal(true);
  }, []);

  // Handler for assigning an anonymous speaker to an existing Dato speaker
  const handleAssignToExistingSpeaker = useCallback(
    async (datoSpeakerId: string): Promise<boolean> => {
      if (!storyId || !selectedAnonymousSpeaker) return false;

      // Suppress CDN re-fetch BEFORE the await — assignSpeaker() fires
      // speaker-cache-invalidated internally before returning.
      recentUpdateRef.current = Date.now();

      const result = await speakerService.assignSpeaker(
        storyId,
        selectedAnonymousSpeaker.speakerLabel,
        datoSpeakerId,
        speakerMappings || {},
      );

      if (result.ok) {
        // Optimistic UI update: move the speaker into the assigned list immediately.
        const matchedSpeaker =
          organizationSpeakers.find(s => s.id === datoSpeakerId) ||
          unassignedSpeakers.find(s => s.id === datoSpeakerId);
        if (matchedSpeaker) {
          setAssignedSpeakers(prev =>
            prev.some(s => s.id === datoSpeakerId)
              ? prev
              : [...prev, matchedSpeaker],
          );
          setUnassignedSpeakers(prev =>
            prev.filter(s => s.id !== datoSpeakerId),
          );
        }
        setAnonymousSpeakers(prev =>
          prev.filter(
            s => s.speakerLabel !== selectedAnonymousSpeaker.speakerLabel,
          ),
        );
        setVoiceMappedDatoIds(prev => new Set([...prev, datoSpeakerId]));

        // Write the new mapping to the label cache so the transcript resolves immediately.
        if (matchedSpeaker) {
          cacheAndWriteSpeakerAssignment(
            storyId,
            selectedAnonymousSpeaker.speakerLabel,
            matchedSpeaker.name,
            matchedSpeaker.color ||
              getSpeakerColorFromLabel(selectedAnonymousSpeaker.speakerLabel),
          );
          notifySpeakerCacheUpdated(storyId);
        }
      } else {
        recentUpdateRef.current = 0;
        logger.error('Error assigning speaker:', result.error);
      }

      setShowAssignModal(false);
      setSelectedAnonymousSpeaker(null);
      return result.ok;
    },
    [storyId, selectedAnonymousSpeaker, speakerMappings, speakerService],
  );

  // Handler for "Create New Speaker" from AssignSpeakerModal
  const handleCreateNewFromAssignModal = useCallback(() => {
    setShowAssignModal(false);
    if (selectedAnonymousSpeaker) {
      setSpeakerLabelToAssign(selectedAnonymousSpeaker.speakerLabel);
      setShowSpeakerModal(true);
    }
  }, [selectedAnonymousSpeaker]);

  // Handler for when speaker identification completes.
  // Directly removes mapped anonymous speakers and schedules a delayed re-fetch.
  const handleIdentificationComplete = useCallback(
    (mappedLabels?: string[]) => {
      // Immediately remove anonymous speakers whose labels were mapped
      if (mappedLabels && mappedLabels.length > 0) {
        const labelSet = new Set(mappedLabels);
        setAnonymousSpeakers(prev =>
          prev.filter(a => !labelSet.has(a.speakerLabel)),
        );
      }
      // Reset the suppression window so the delayed refresh isn't blocked
      recentUpdateRef.current = 0;
      // Delay to let DatoCMS CDN propagate the new mappings
      setTimeout(() => {
        setRefreshCounter(prev => prev + 1);
      }, 3000);
    },
    [],
  );

  // Jump to a transcript section for a given speaker label.
  // 'current' = land on current or nearest section (row click default)
  // 'next' / 'prev' = explicit navigation arrows
  const handleJumpToSpeaker = useCallback(
    (
      speakerLabel: string,
      direction: 'current' | 'next' | 'prev' = 'current',
    ) => {
      // Use original transcript elements which have proper timestamps (ts),
      // unlike getFinalTranscriptionElements() which may lack them.
      const elements = transcriptionStore?.getOriginalTranscription()?.elements;
      if (!elements) return;

      // In Content Studio's T&S tab the player shows story.originalVideo, so
      // navigation must search the full transcript even if currentVideo points
      // at a stale clip. In Creator Studio currentVideo is the edited clip
      // and we want to confine navigation to its transcript range.
      const clipRange = useOriginalTranscript
        ? undefined
        : playbackCreator.currentVideo?.clipJson?.transcriptPosition;
      const relevantElements = clipRange
        ? elements.slice(clipRange.startIndex, clipRange.endIndex + 1)
        : elements;

      const groups = groupElementsByParagraph(relevantElements);

      // The groups' startTimes are story-absolute, so the playhead has to be
      // read in that space too before they can be compared (PLAT-11389).
      const referenceTime = playbackCreator.time + hostTimeOffset;

      let targetGroup;
      if (direction === 'prev') {
        targetGroup = findPreviousSpeakerSection(
          groups,
          speakerLabel,
          referenceTime,
        );
      } else if (direction === 'next') {
        targetGroup = findNextSpeakerSection(
          groups,
          speakerLabel,
          referenceTime,
        );
      } else {
        targetGroup = findCurrentOrNextSpeakerSection(
          groups,
          speakerLabel,
          referenceTime,
        );
      }

      if (targetGroup?.startTime != null) {
        // ...and back into the host's space on the way out.
        const seekTime = targetGroup.startTime - hostTimeOffset;
        // Dispatch event so TranscriptVideoPlayer can seek directly
        // (setting the time alone is blocked by RAF feedback prevention
        // during playback). Scoped to the HOST's creator: this panel mounts
        // inside the Translation Review workspace and the T&S tab too, and a
        // jump-to-speaker there must move that surface's player and no other
        // (PLAT-10286).
        dispatchTranscriptSeek(seekTime, playbackCreator);
        // Also set time directly as fallback for transcript cursor positioning
        // (transcript-seek only works if MuxPlayer is loaded and has valid duration)
        playbackCreator.setTime(seekTime, true);
      }
    },
    [
      transcriptionStore,
      playbackCreator,
      hostTimeOffset,
      useOriginalTranscript,
    ],
  );

  // Resolve speaker label from Dato speaker ID via reverse mapping lookup.
  // Falls back to override_<datoId> for speakers assigned via section override.
  const getSpeakerLabelForDatoId = useCallback(
    (datoId: string): string | undefined => {
      // Check voice mappings first (spk_X → datoId)
      if (speakerMappings) {
        const voiceLabel = Object.entries(speakerMappings).find(
          ([, id]) => id === datoId,
        )?.[0];
        if (voiceLabel) return voiceLabel;
      }
      // Fallback: section override label
      return `override_${datoId}`;
    },
    [speakerMappings],
  );

  if (isLoading) {
    return (
      <Container>
        <LoadingText>Loading speakers...</LoadingText>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorText>{error}</ErrorText>
      </Container>
    );
  }

  if (!storyId) {
    return (
      <Container>
        <EmptyState>No story loaded</EmptyState>
      </Container>
    );
  }

  // Deduplicate speaker lists by ID (optimistic updates + CDN fetch can create temporary dupes)
  const deduped = <T extends { id: string }>(arr: T[]) => {
    const seen = new Set<string>();
    return arr.filter(s => (seen.has(s.id) ? false : (seen.add(s.id), true)));
  };
  const dedupedAssigned = deduped(assignedSpeakers);
  const dedupedUnassigned = deduped(unassignedSpeakers);

  // Context menu visibility flags
  const speakerId = selectedSpeaker?.id || '';
  const isAssignedStoryContext =
    activeTab === 'story' &&
    !selectedAnonymousSpeaker &&
    dedupedAssigned.some(s => s.id === speakerId);
  const hasVoiceMapping = voiceMappedDatoIds.has(speakerId);
  const hasOverride = activeOverrideLabels.includes(`override_${speakerId}`);

  return (
    <Container data-testid="speakers-panel">
      {props.headerActions && (
        <Header>
          <HeaderButtons>{props.headerActions}</HeaderButtons>
        </Header>
      )}

      {/* The scope toggle and the panel's own actions share one row, so the
          list starts right under the panel's tabs. */}
      <Toolbar>
        <ToggleGroup<TabType>
          options={[
            { id: 'story', label: 'Story', count: allStorySpeakers.length },
            {
              id: 'organization',
              label: 'Organization',
              count: organizationSpeakers.length,
            },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Speaker scope"
          testIdPrefix="speakers-tab"
        />
        <ToolbarActions>
          {enableSpeakersIdentification &&
            activeTab === 'story' &&
            anonymousSpeakers.length > 0 && (
              <>
                <IdSpeakersButton
                  onClick={() => setShowIdentificationModal(true)}
                  aria-label="ID Speaker"
                  data-tooltip-id={idSpeakerTooltipId}
                  data-tooltip-content="ID Speaker"
                >
                  <AiSpeakerIcon />
                </IdSpeakersButton>
                <Tooltip
                  id={idSpeakerTooltipId}
                  place="bottom"
                  style={TOOLTIP_STYLE}
                />
              </>
            )}
          <AddSpeakerButton
            onClick={handleOpenAddSpeakerModal}
            aria-label="Add Speaker"
          >
            <PlusIcon
              width="10"
              height="10"
              viewBox="4 4 32 32"
              strokeColor="currentColor"
              strokeWidth={4}
            />
            Speaker
          </AddSpeakerButton>
        </ToolbarActions>
      </Toolbar>

      <SpeakersList>
        {activeTab === 'story' ? (
          <>
            {/* Optimistic "saving" rows for in-flight Add Speaker creates */}
            {pendingSpeakers.map(pending => (
              <PendingRow
                key={pending.tempId}
                data-testid={`speaker-card-pending-${pending.tempId}`}
              >
                <SpeakerAvatar
                  name={pending.name}
                  color={pending.color}
                  headshotUrl={pending.previewUrl}
                />
                <SpeakerInfo>
                  <SpeakerName $color={pending.color}>
                    {pending.name}
                  </SpeakerName>
                  {pending.status === 'error' ? (
                    <SavingErrorText>Failed to save</SavingErrorText>
                  ) : (
                    <SavingText>Saving…</SavingText>
                  )}
                </SpeakerInfo>
                {pending.status === 'error' ? (
                  <RowActions>
                    <RetryButton
                      onClick={() => handleRetryPendingSpeaker(pending)}
                    >
                      Retry
                    </RetryButton>
                    <DismissButton
                      onClick={() =>
                        handleDismissPendingSpeaker(pending.tempId)
                      }
                      title="Dismiss"
                    >
                      <CloseIcon />
                    </DismissButton>
                  </RowActions>
                ) : (
                  <SavingSpinner aria-label="Saving" />
                )}
              </PendingRow>
            ))}

            {/* Assigned Section - hidden when empty */}
            {dedupedAssigned.length > 0 && (
              <>
                <SectionHeader>Assigned</SectionHeader>
                {dedupedAssigned.map(speaker => (
                  <SpeakerRow
                    key={speaker.id}
                    data-testid={`speaker-card-${speaker.id}`}
                    onClick={() => {
                      const label = getSpeakerLabelForDatoId(speaker.id);
                      if (label) handleJumpToSpeaker(label);
                    }}
                  >
                    <SpeakerAvatar
                      name={speaker.name}
                      color={speaker.color}
                      headshotUrl={speaker.headshots?.[0]?.url}
                    />
                    <SpeakerInfo>
                      <SpeakerName $color={speaker.color}>
                        {speaker.name}
                      </SpeakerName>
                      {speaker.title && (
                        <SpeakerTitle>{speaker.title}</SpeakerTitle>
                      )}
                    </SpeakerInfo>
                    <RowActions>
                      <NavButton
                        onClick={e => {
                          e.stopPropagation();
                          const label = getSpeakerLabelForDatoId(speaker.id);
                          if (label) handleJumpToSpeaker(label, 'prev');
                        }}
                        title="Previous mention"
                      >
                        ‹
                      </NavButton>
                      <NavButton
                        onClick={e => {
                          e.stopPropagation();
                          const label = getSpeakerLabelForDatoId(speaker.id);
                          if (label) handleJumpToSpeaker(label, 'next');
                        }}
                        title="Next mention"
                      >
                        ›
                      </NavButton>
                      <OverflowButton
                        onClick={e => handleOverflowButtonClick(e, speaker)}
                        title="Speaker options"
                        data-testid={`speaker-options-button-${speaker.id}`}
                      >
                        <OverflowButtonIcon>
                          <MoreVertIcon width="14" height="14" />
                        </OverflowButtonIcon>
                      </OverflowButton>
                    </RowActions>
                  </SpeakerRow>
                ))}
              </>
            )}

            {/* Anonymous Section - Transcript speakers without Dato mapping */}
            {anonymousSpeakers.length > 0 && (
              <>
                <SectionHeader>Anonymous</SectionHeader>
                {anonymousSpeakers.map(speaker => (
                  <SpeakerRow
                    key={speaker.speakerLabel}
                    data-testid={`speaker-card-${speaker.speakerLabel}`}
                    onClick={() => handleJumpToSpeaker(speaker.speakerLabel)}
                  >
                    <SpeakerAvatar name={speaker.name} color={speaker.color} />
                    <SpeakerInfo>
                      <SpeakerName $color={speaker.color}>
                        {speaker.name}
                      </SpeakerName>
                      <SpeakerTitle>Not assigned</SpeakerTitle>
                    </SpeakerInfo>
                    <RowActions>
                      <NavButton
                        onClick={e => {
                          e.stopPropagation();
                          handleJumpToSpeaker(speaker.speakerLabel, 'prev');
                        }}
                        title="Previous mention"
                      >
                        ‹
                      </NavButton>
                      <NavButton
                        onClick={e => {
                          e.stopPropagation();
                          handleJumpToSpeaker(speaker.speakerLabel, 'next');
                        }}
                        title="Next mention"
                      >
                        ›
                      </NavButton>
                      <OverflowButton
                        onClick={e =>
                          handleAnonymousOverflowButtonClick(e, speaker)
                        }
                        title="Assign speaker"
                        data-testid={`speaker-options-button-${speaker.speakerLabel}`}
                      >
                        <OverflowButtonIcon>
                          <MoreVertIcon width="14" height="14" />
                        </OverflowButtonIcon>
                      </OverflowButton>
                    </RowActions>
                  </SpeakerRow>
                ))}
              </>
            )}

            {/* Unassigned Section - hidden when empty */}
            {dedupedUnassigned.length > 0 && (
              <>
                <SectionHeader>Unassigned</SectionHeader>
                {dedupedUnassigned.map(speaker => (
                  <SpeakerRow
                    key={speaker.id}
                    data-testid={`speaker-card-${speaker.id}`}
                  >
                    <SpeakerAvatar
                      name={speaker.name}
                      color={speaker.color}
                      headshotUrl={speaker.headshots?.[0]?.url}
                    />
                    <SpeakerInfo>
                      <SpeakerName $color={speaker.color}>
                        {speaker.name}
                      </SpeakerName>
                      {speaker.title && (
                        <SpeakerTitle>{speaker.title}</SpeakerTitle>
                      )}
                    </SpeakerInfo>
                    <OverflowButton
                      onClick={e => handleOverflowButtonClick(e, speaker)}
                      title="Speaker options"
                      data-testid={`speaker-options-button-${speaker.id}`}
                    >
                      <OverflowButtonIcon>
                        <MoreVertIcon width="14" height="14" />
                      </OverflowButtonIcon>
                    </OverflowButton>
                  </SpeakerRow>
                ))}
              </>
            )}
          </>
        ) : /* Organization Tab */
        organizationSpeakers.length === 0 ? (
          <EmptyState>No organization speakers available</EmptyState>
        ) : (
          organizationSpeakers.map(speaker => (
            <SpeakerRow
              key={speaker.id}
              data-testid={`speaker-card-${speaker.id}`}
            >
              <SpeakerAvatar
                name={speaker.name}
                color={speaker.color}
                headshotUrl={speaker.headshots?.[0]?.url}
              />
              <SpeakerInfo>
                <SpeakerName $color={speaker.color}>{speaker.name}</SpeakerName>
                {speaker.title && <SpeakerTitle>{speaker.title}</SpeakerTitle>}
              </SpeakerInfo>
              {!isSpeakerInStory(speaker.id) ? (
                <AddButton onClick={() => handleAddToStory(speaker)}>
                  + Add
                </AddButton>
              ) : (
                <AddedLabel>Added</AddedLabel>
              )}
              <OverflowButton
                onClick={e => handleOverflowButtonClick(e, speaker)}
                title="Speaker options"
                data-testid={`speaker-options-button-${speaker.id}`}
              >
                <OverflowButtonIcon>
                  <MoreVertIcon width="14" height="14" />
                </OverflowButtonIcon>
              </OverflowButton>
            </SpeakerRow>
          ))
        )}
      </SpeakersList>

      <SpeakerFormModal
        isOpen={showSpeakerModal}
        onClose={handleModalClose}
        onSubmit={speakerToEdit ? handleUpdateSpeaker : handleCreateSpeaker}
        onSubmitBackground={handleCreateSpeakerBackground}
        speakerToEdit={
          speakerToEdit
            ? {
                speakerLabel: '',
                datoSpeakerId: speakerToEdit.id,
                name: speakerToEdit.name,
                title: speakerToEdit.title,
                bio: speakerToEdit.bio,
                voice: speakerToEdit.voice,
                gender: speakerToEdit.gender,
                headshot: speakerToEdit.headshots?.[0]?.url || null,
                headshots: speakerToEdit.headshots || [],
              }
            : undefined
        }
      />

      <SpeakerContextMenu
        isOpen={contextMenuOpen}
        buttonRef={activeButtonRef}
        onClose={handleCloseContextMenu}
        onEdit={handleEditSpeaker}
        onManageAiVoices={handleManageAiVoices}
        onUnassign={handleUnassignSpeaker}
        onUndoSectionReassign={handleUndoSectionReassign}
        onUnassignAll={handleUnassignAll}
        onReassignAll={handleReassignAll}
        onRemoveFromStory={handleRemoveFromStory}
        onRemoveFromOrganization={handleRemoveFromOrganization}
        onAssignToExisting={handleOpenAssignModal}
        isAnonymous={!!selectedAnonymousSpeaker}
        showAddToStory={false}
        showRemoveFromStory={
          activeTab === 'story' &&
          !selectedAnonymousSpeaker &&
          dedupedUnassigned.some(s => s.id === selectedSpeaker?.id)
        }
        showUnassign={isAssignedStoryContext && hasVoiceMapping}
        showUndoSectionReassign={isAssignedStoryContext && hasOverride}
        showUnassignAll={
          isAssignedStoryContext && hasVoiceMapping && hasOverride
        }
        showReassignAll={
          isAssignedStoryContext && hasVoiceMapping && hasOverride
        }
        showRemoveFromOrganization={
          activeTab === 'organization' && !selectedAnonymousSpeaker
        }
        showManageAiVoices={
          enableAiVoices && dubbingEnabled && !selectedAnonymousSpeaker
        }
      />

      {manageVoicesSpeaker && (
        <ManageAiVoicesModal
          isOpen
          onClose={() => setManageVoicesSpeaker(null)}
          speakerId={manageVoicesSpeaker.id}
          speakerName={manageVoicesSpeaker.name}
          speakerHeadshotUrl={manageVoicesSpeaker.headshots?.[0]?.url}
          speakerColor={manageVoicesSpeaker.color}
        />
      )}

      {showAssignModal && selectedAnonymousSpeaker && (
        <AssignSpeakerModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedAnonymousSpeaker(null);
          }}
          onAssign={handleAssignToExistingSpeaker}
          storySpeakers={[...dedupedAssigned, ...dedupedUnassigned]}
          organizationSpeakers={organizationSpeakers}
          speakerLabel={selectedAnonymousSpeaker.speakerLabel}
          speakerDisplayName={selectedAnonymousSpeaker.name}
          onCreateNewSpeaker={handleCreateNewFromAssignModal}
        />
      )}

      {showIdentificationModal && (
        <CreatorStudioSpeakerIdentificationModal
          isOpen={showIdentificationModal}
          onClose={() => setShowIdentificationModal(false)}
          anonymousSpeakers={anonymousSpeakers}
          currentMappings={speakerMappings}
          onIdentificationComplete={handleIdentificationComplete}
        />
      )}
    </Container>
  );
});

export default CreatorStudioSpeakersPanel;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Only a host's headerActions live here. The panel card's body has no top
     padding, so without this the row sits flush against the header divider. */
  margin-top: var(--spacing-sm);
`;

/* PLAT-11877 — a label never breaks across lines; when the row is too
   narrow for every button (the 360px column with ID Speaker up), whole
   buttons wrap to a second row instead. */
const HeaderButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  > * {
    white-space: nowrap;
  }
`;

const IdSpeakersButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--btn-h-xs);
  height: var(--btn-h-xs);
  padding: 0;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  cursor: pointer;
  transition: all 150ms ease-out;

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;

const AddSpeakerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  height: var(--btn-h-xs);
  padding: 0 var(--btn-px-xs);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition: all 150ms ease-out;

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: var(--spacing-sm) 0;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

const SpeakersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  flex: 1;
`;

const SectionHeader = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
  /* PLAT-11877 — the list's own --spacing-sm gap is the label-to-row space;
     a later group's label takes one more --spacing-sm above it, so groups
     read as groups (16px) while a label hugs its rows (8px). */
  margin: var(--spacing-sm) 0 0 0;

  &:first-child {
    margin-top: 0;
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: var(--radius-input);
  opacity: 0;
  transition:
    opacity 0.15s ease,
    color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--sidebar-active);
  }
`;

const OverflowButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: var(--text-lg);
  cursor: pointer;
  padding: var(--spacing-xs);
  flex-shrink: 0;

  &:hover {
    color: var(--foreground);
    background-color: var(--sidebar-active);
    border-radius: var(--radius-input);
  }
`;

const OverflowButtonIcon = styled.div`
  width: 22px;
  text-align: center;
`;

const SpeakerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--sidebar-hover);
  border-radius: var(--radius);
  transition: background var(--transition-normal) ease;
  cursor: pointer;

  &:hover {
    background: var(--sidebar-active);

    ${NavButton} {
      opacity: 1;
    }
  }
`;

// Skeleton row for an in-flight create — same shape as SpeakerRow but inert.
const PendingRow = styled(SpeakerRow)`
  cursor: default;

  &:hover {
    background: var(--sidebar-hover);
  }
`;

const SavingText = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: 1.2;
`;

const SavingErrorText = styled.span`
  font-size: var(--text-xs);
  color: var(--destructive);
  line-height: 1.2;
`;

const RetryButton = styled.button`
  padding: var(--spacing-2xs) var(--spacing-sm);
  border-radius: var(--radius-input);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    border-color: var(--text-muted);
    background: var(--surface-elevated);
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--radius-input);
  flex-shrink: 0;

  &:hover {
    color: var(--foreground);
    background-color: var(--sidebar-active);
  }
`;

const SavingSpinner = styled.div`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: speaker-saving-spin 0.8s linear infinite;

  @keyframes speaker-saving-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SpeakerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const SpeakerName = styled.span<{ $color?: string }>`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: ${props => props.$color || 'var(--foreground)'};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpeakerTitle = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AddButton = styled.button`
  /* Secondary, matching AddSpeakerButton in this same file. It used to be
     transparent with both border and color set to --primary — the green-outline
     pattern the design system names and says to neutralise — at a
     padding-derived height off the 32/36/40/48 scale, with a green wash on
     hover. */
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background-color var(--transition-fast) ease,
    border-color var(--transition-fast) ease;
  flex-shrink: 0;

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

/**
 * Occupies the same box as the AddButton it replaces, so the row doesn't shift
 * when a speaker is added — but borderless and muted, because it reports a state
 * rather than offering an action.
 */
const AddedLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  font-size: var(--text-xs);
  color: var(--text-muted);
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: var(--spacing-lg) var(--spacing-md);
`;

const LoadingText = styled.div`
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: var(--spacing-lg) var(--spacing-md);
`;

const ErrorText = styled.div`
  text-align: center;
  color: var(--destructive);
  font-size: var(--text-xs);
  padding: var(--spacing-lg) var(--spacing-md);
`;
